// conciliacionMatch.js — sugerencias de conciliación banco↔factura,
// compartidas entre BancosView.js (pestaña Movimientos) y la pestaña
// Facturas (para poder sugerir también desde el listado de facturas, no
// solo desde Bancos). Antes solo se admitía como candidata una factura con
// el importe dentro de ±5% — el usuario pidió que también cuente la
// similitud del nombre del cliente con el concepto del movimiento, aunque
// el importe no cuadre exactamente (pagos parciales, comisiones descontadas...).

// Normaliza para comparar: mayúsculas, sin tildes, solo letras/números/espacios.
function normalizar(s) {
  return String(s || '')
    .toUpperCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Similitud por solapamiento de palabras — nada de librerías de fuzzy-match
// para comparar dos textos cortos. Cuenta cuántas palabras "significativas"
// (3+ letras, para no contar preposiciones/iniciales sueltas) del nombre del
// cliente aparecen tal cual dentro del concepto del movimiento bancario.
function similitudNombre(concepto, nombre) {
  const cWords = new Set(normalizar(concepto).split(' ').filter(w => w.length >= 3));
  const nWords = normalizar(nombre).split(' ').filter(w => w.length >= 3);
  if (!nWords.length || !cWords.size) return 0;
  const coincide = nWords.filter(w => cWords.has(w)).length;
  return coincide / nWords.length;
}

const UMBRAL_IMPORTE = 0.05; // ±5%
const UMBRAL_NOMBRE  = 0.5;  // al menos la mitad de las palabras del nombre

// Compara un par (movimiento, factura) y devuelve la ficha de coincidencia,
// o null si ninguno de los dos criterios (importe / nombre) se cumple.
// Compartido por las dos direcciones de sugerencia de más abajo.
function compararPar(mov, f) {
  const imp     = Math.abs(Number(mov.importe) || 0);
  const fImp    = Math.abs(Number(f.importeTotal ?? f.importe ?? 0));
  const impDiff = Math.abs(fImp - imp);
  const impRel  = imp ? impDiff / imp : 1;
  const porImporte = impRel < UMBRAL_IMPORTE;

  const textScore = similitudNombre(mov.concepto, f.clienteNombre || '');
  const porNombre  = textScore >= UMBRAL_NOMBRE;

  if (!porImporte && !porNombre) return null;

  const movTime = new Date(mov.fecha).getTime();
  const fTime   = new Date(f.fecha || f.cobro?.fecha || 0).getTime();
  const dias    = (movTime && fTime) ? Math.abs(movTime - fTime) / 86400000 : 999;

  const motivo = porImporte && porNombre ? 'importe+nombre' : porImporte ? 'importe' : 'nombre';
  // Menor score = mejor sugerencia. El importe sigue pesando más que el
  // nombre — una coincidencia de nombre fuerte resta puntos pero no
  // compensa una diferencia de importe grande.
  const score = (porImporte ? impRel * 100 : 40) + Math.min(dias, 90) * 0.3 - textScore * 20;

  return { dias, exacto: impDiff < 0.01, textScore, motivo, score };
}

// Devuelve hasta 10 facturas candidatas para conciliar con `mov`, ordenadas
// de mejor a peor sugerencia. Cada resultado indica `motivo`
// ('importe' | 'nombre' | 'importe+nombre') para que la UI explique por qué
// se sugiere, no solo que se sugiere. Usado en BancosView.js (pestaña
// Movimientos: "dado este movimiento, ¿qué factura le corresponde?").
export function sugerirCoincidencias(mov, facturas) {
  if (!mov || !facturas?.length) return [];
  return facturas
    .map(f => {
      const par = compararPar(mov, f);
      return par && { factura: f, ...par };
    })
    .filter(Boolean)
    .sort((a, b) => a.score - b.score)
    .slice(0, 10);
}

// Dirección inversa: dada una factura, ¿qué movimiento bancario sin conciliar
// le corresponde? Usado en FacturasView.js (pestaña Pendiente: sugerir el
// pago sin tener que ir a Bancos a buscarlo).
export function sugerirMovimientoParaFactura(factura, movimientos) {
  if (!factura || !movimientos?.length) return null;
  const candidatos = movimientos
    .map(mov => {
      const par = compararPar(mov, factura);
      return par && { movimiento: mov, ...par };
    })
    .filter(Boolean)
    .sort((a, b) => a.score - b.score);
  return candidatos[0] || null;
}

export const MOTIVO_LABEL = {
  'importe+nombre': 'importe y nombre',
  importe: 'importe',
  nombre: 'nombre del cliente',
};
